import { useState, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

interface Option {
    value: string;
    label: string;
    sublabel?: string;
}

interface SearchableMultiSelectProps {
    values: string[];
    onValuesChange: (values: string[]) => void;
    options: Option[];
    placeholder?: string;
    searchPlaceholder?: string;
    className?: string;
    disabled?: boolean;
    showTags?: boolean;
    selectedLabel?: (count: number) => string;
}

export function SearchableMultiSelect({
    values,
    onValuesChange,
    options,
    placeholder = 'Selecione...',
    searchPlaceholder = 'Buscar...',
    className,
    disabled,
    showTags = true,
    selectedLabel,
}: SearchableMultiSelectProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const filtered = options.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase()) ||
        (o.sublabel && o.sublabel.toLowerCase().includes(search.toLowerCase()))
    );

    useEffect(() => {
        if (open) {
            setSearch('');
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open]);

    const toggle = (value: string) => {
        if (values.includes(value)) {
            onValuesChange(values.filter((v) => v !== value));
        } else {
            onValuesChange([...values, value]);
        }
    };

    const remove = (value: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onValuesChange(values.filter((v) => v !== value));
    };

    const selectedOptions = options.filter((o) => values.includes(o.value));

    return (
        <div className="space-y-2">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className={cn(
                            'w-full justify-between font-normal rounded-xl border-gray-200 bg-white hover:bg-gray-50 h-auto min-h-10',
                            !values.length && 'text-gray-400',
                            className
                        )}
                    >
                        <span className="truncate">
                            {values.length === 0
                                ? placeholder
                                : selectedLabel
                                    ? selectedLabel(values.length)
                                    : `${values.length} selecionado${values.length > 1 ? 's' : ''}`}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                    </Button>
                </PopoverTrigger>

                <PopoverContent
                    className="p-0 rounded-xl border border-gray-100 shadow-xl bg-white"
                    style={{ width: 'var(--radix-popover-trigger-width)' }}
                    align="start"
                >
                    <div className="flex items-center border-b border-gray-100 px-3 py-2 gap-2">
                        <Search className="h-4 w-4 shrink-0 text-gray-400" />
                        <input
                            ref={inputRef}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="flex-1 text-sm outline-none bg-transparent placeholder:text-gray-400"
                        />
                    </div>

                    <div className="max-h-60 overflow-y-auto py-1">
                        {filtered.length === 0 ? (
                            <p className="px-4 py-6 text-center text-sm text-gray-400">
                                Nenhum resultado encontrado.
                            </p>
                        ) : (
                            filtered.map((option) => {
                                const selected = values.includes(option.value);
                                return (
                                    <button
                                        key={option.value}
                                        type="button"
                                        onClick={() => toggle(option.value)}
                                        className={cn(
                                            'w-full flex items-center justify-between px-4 py-2.5 text-sm text-left transition-colors hover:bg-gray-50',
                                            selected && 'bg-blue-50'
                                        )}
                                    >
                                        <div className="flex flex-col">
                                            <span className={cn('font-medium', selected && 'text-blue-700')}>
                                                {option.label}
                                            </span>
                                            {option.sublabel && (
                                                <span className="text-xs text-gray-400">{option.sublabel}</span>
                                            )}
                                        </div>
                                        {selected && <Check className="h-4 w-4 text-blue-600 shrink-0" />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            {showTags && selectedOptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selectedOptions.map((o) => (
                        <span
                            key={o.value}
                            className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full border border-blue-200/60"
                        >
                            {o.label}
                            <button
                                type="button"
                                onClick={(e) => remove(o.value, e)}
                                className="hover:text-blue-900 transition-colors"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
